import React from 'react'
import type { JSONContent } from '../types'
import TextField from '@mui/material/TextField'
import FormControl from '@mui/material/FormControl'
import Typography from '@mui/material/Typography'
import InputAdornment from '@mui/material/InputAdornment'
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardMedia from '@mui/material/CardMedia'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import ImageIcon from '@mui/icons-material/Image'
import CloseIcon from '@mui/icons-material/Close'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import dayjs, { type Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { parseStyleString } from '../utils'

dayjs.extend(utc)
import { convertStylesToSx } from '../utils'
import type { CurrentSchema } from '../types'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import type { EditorLayout } from '../types'
import { PhoneInputWithCountry } from './PhoneInputWithCountry'

interface RenderFieldInputProps {
  node: JSONContent
  type: string
  formValues?: Record<string, any>
  onFieldChange?: (name: string, value: any) => void
  currentSchema?: CurrentSchema
  validationErrors?: Record<string, string>
  content?: JSONContent // Полный content для проверки наличия label
  layout?: EditorLayout
  onValidationError?: (fieldName: string, error: string) => void
  /** Callback для автосохранения при потере фокуса */
  onBlur?: () => void
}

export function RenderFieldInput({
  node,
  type,
  formValues,
  onFieldChange,
  currentSchema,
  validationErrors,
  content,
  layout,
  onValidationError,
  onBlur,
}: RenderFieldInputProps) {
  const attrs = node.attrs || {}
  const fieldId = attrs.fieldId || ''

  // Получаем настройки из layout.fields
  const fieldSettings = fieldId && layout?.fields?.[fieldId]

  // Используем настройки из layout.fields, если они есть, иначе из node.attrs
  const name =
    fieldSettings?.name ||
    attrs.name ||
    fieldId ||
    `field-${fieldId || 'unknown'}`
  const placeholder = fieldSettings?.placeholder || attrs.placeholder || ''
  const required = fieldSettings?.required ?? attrs.required ?? false
  const placeholderEnabled =
    fieldSettings?.placeholderEnabled ?? attrs.placeholderEnabled !== false
  const hidden = fieldSettings?.hidden ?? attrs.hidden ?? false
  const defaultValue = fieldSettings?.defaultValue || attrs.defaultValue || ''
  const defaultValueEnabled =
    fieldSettings?.defaultValueEnabled ?? attrs.defaultValueEnabled ?? false
  const minLength = fieldSettings?.minLength ?? attrs.minLength
  const maxLength = fieldSettings?.maxLength ?? attrs.maxLength
  const minLengthEnabled =
    fieldSettings?.minLengthEnabled ?? attrs.minLengthEnabled ?? false
  const maxLengthEnabled =
    fieldSettings?.maxLengthEnabled ?? attrs.maxLengthEnabled ?? false
  const labelEnabled =
    fieldSettings?.labelEnabled ?? attrs.labelEnabled !== false
  const patternEnabled =
    fieldSettings?.patternEnabled ?? attrs.patternEnabled ?? false
  const mask = fieldSettings?.mask || attrs.mask
  const defaultCountryEnabled =
    fieldSettings?.defaultCountryEnabled ?? attrs.defaultCountryEnabled ?? false
  const defaultCountry = defaultCountryEnabled
    ? fieldSettings?.defaultCountry || attrs.defaultCountry
    : undefined
  const fieldStyle = convertStylesToSx(parseStyleString(attrs.style as string))

  // Используем mask только если patternEnabled и mask задан
  const shouldUseMask = patternEnabled && mask

  // Для phone field используем специальный компонент с выбором страны
  const isPhoneField = node.type === 'phoneNumberFieldInput'

  // Проверяем, есть ли label для этого поля
  let hasLabel = labelEnabled
  if (content && fieldId) {
    function findLabel(node: JSONContent) {
      if (node.type === 'fieldLabel') {
        const labelAttrs = node.attrs || {}
        if (labelAttrs.fieldId === fieldId) {
          hasLabel = true

          return
        }
      }
      if (node.content) {
        node.content.forEach(findLabel)
      }
    }
    findLabel(content)
  }

  // Применяем currentSchema для скрытия/показа полей
  // Извлекаем информацию из JSON-schema
  const fieldSchema = currentSchema?.properties?.[name]
  const isHiddenBySchema = fieldSchema?.meta?.hidden === true
  const isShownBySchema = fieldSchema?.meta?.hidden === false
  const isRequiredBySchema = currentSchema?.required?.includes(name) || false

  // Поле скрыто, если:
  // - meta.hidden !== false (нет явного "показать") И
  // - (hidden === true ИЛИ meta.hidden === true)
  const isHiddenByConditions = isHiddenBySchema && !isShownBySchema
  const isFieldHidden = isShownBySchema ? false : hidden || isHiddenByConditions

  if (isFieldHidden) {
    return null
  }

  // Поле required, если: required === true ИЛИ isRequiredBySchema === true
  const isRequired = required || isRequiredBySchema
  const error = validationErrors?.[name]

  // Логируем для отладки
  if (error) {
    console.log('RenderFieldInput error:', {
      name,
      error,
      validationErrors,
      fieldId,
    })
  }

  const inputId = `field-${fieldId || name}`

  const handleValidationError = React.useCallback(
    (validationError: string) => {
      if (onValidationError) {
        onValidationError(name, validationError)
      }
    },
    [onValidationError, name]
  )

  // Для файловых полей value не используется напрямую (uncontrolled),
  // но мы храним локальное состояние выбранных файлов для отображения карточек
  const [fileList, setFileList] = React.useState<File[]>([])

  React.useEffect(() => {
    const raw = formValues?.[name]
    if (raw instanceof File) {
      setFileList([raw])
    } else if (Array.isArray(raw)) {
      setFileList(raw.filter((f) => f instanceof File))
    } else {
      setFileList([])
    }
  }, [formValues, name])

  // Для полей даты/времени получаем значение как Dayjs объект или null
  const getDateValue = (): Dayjs | null => {
    if (type === 'date' || type === 'time' || type === 'datetime-local') {
      const rawValue = formValues?.[name]
      if (rawValue) {
        if (typeof rawValue === 'string') {
          // Для DatePicker: формат "YYYY-MM-DD"
          if (type === 'date') {
            const dateValue = dayjs(rawValue, 'YYYY-MM-DD', true)
            if (dateValue.isValid()) {
              return dateValue.startOf('day')
            }
            // Fallback: пытаемся парсить как ISO строку
            const isoDate = dayjs(rawValue)
            if (isoDate.isValid()) {
              return isoDate.startOf('day')
            }

            return null
          }

          // Для TimePicker: формат "HH:mm:ss+00:00" или "HH:mm:ssZ"
          if (type === 'time') {
            // Парсим время с UTC offset
            let timeValue: Dayjs | null = null
            // Пробуем формат "HH:mm:ss+00:00"
            if (rawValue.includes('+')) {
              const [timePart] = rawValue.split('+')
              timeValue = dayjs.utc(
                `2000-01-01T${timePart}`,
                'YYYY-MM-DDTHH:mm:ss'
              )
            } else if (rawValue.endsWith('Z')) {
              // Формат "HH:mm:ssZ"
              const timePart = rawValue.replace('Z', '')
              timeValue = dayjs.utc(
                `2000-01-01T${timePart}`,
                'YYYY-MM-DDTHH:mm:ss'
              )
            } else {
              // Просто время "HH:mm:ss" - считаем что это UTC
              timeValue = dayjs.utc(
                `2000-01-01T${rawValue}`,
                'YYYY-MM-DDTHH:mm:ss'
              )
            }

            if (timeValue && timeValue.isValid()) {
              // Конвертируем UTC время в локальное для отображения
              const localTime = timeValue.local()

              return dayjs()
                .hour(localTime.hour())
                .minute(localTime.minute())
                .second(localTime.second())
                .millisecond(localTime.millisecond())
            }
            // Fallback: пытаемся парсить как ISO строку
            const isoTime = dayjs.utc(rawValue).local()
            if (isoTime.isValid()) {
              return dayjs()
                .hour(isoTime.hour())
                .minute(isoTime.minute())
                .second(isoTime.second())
                .millisecond(isoTime.millisecond())
            }

            return null
          }

          // Для DateTimePicker: ISO строка
          const utcDate = dayjs.utc(rawValue)
          if (utcDate.isValid()) {
            return utcDate.local()
          }

          return null
        }
      }
      // Если есть defaultValue, пытаемся его использовать
      if (defaultValueEnabled && defaultValue) {
        if (type === 'date') {
          const defaultDate = dayjs(defaultValue, 'YYYY-MM-DD', true)
          if (defaultDate.isValid()) {
            return defaultDate.startOf('day')
          }
          const isoDate = dayjs(defaultValue)
          if (isoDate.isValid()) {
            return isoDate.startOf('day')
          }
        } else if (type === 'time') {
          let timeValue: Dayjs | null = null
          if (defaultValue.includes('+')) {
            const [timePart] = defaultValue.split('+')
            timeValue = dayjs.utc(
              `2000-01-01T${timePart}`,
              'YYYY-MM-DDTHH:mm:ss'
            )
          } else if (defaultValue.endsWith('Z')) {
            const timePart = defaultValue.replace('Z', '')
            timeValue = dayjs.utc(
              `2000-01-01T${timePart}`,
              'YYYY-MM-DDTHH:mm:ss'
            )
          } else {
            timeValue = dayjs.utc(
              `2000-01-01T${defaultValue}`,
              'YYYY-MM-DDTHH:mm:ss'
            )
          }
          if (timeValue && timeValue.isValid()) {
            const localTime = timeValue.local()

            return dayjs()
              .hour(localTime.hour())
              .minute(localTime.minute())
              .second(localTime.second())
              .millisecond(localTime.millisecond())
          }
        } else {
          const defaultDate = dayjs.utc(defaultValue).local()
          if (defaultDate.isValid()) {
            return defaultDate
          }
        }

        return null
      }

      return null
    }

    return null
  }

  // Для остальных полей убеждаемся, что value всегда строка (не undefined)
  const getValue = () => {
    if (type === 'file') {
      return undefined
    }
    if (type === 'date' || type === 'time' || type === 'datetime-local') {
      return getDateValue()
    }
    if (formValues?.[name] !== undefined) {
      return String(formValues[name] || '')
    }

    return defaultValueEnabled ? defaultValue : ''
  }

  const value = getValue()
  const dateValue =
    type === 'date' || type === 'time' || type === 'datetime-local'
      ? (value as Dayjs | null)
      : null

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newValue = e.target.value
    if (onFieldChange && name) {
      onFieldChange(name, newValue)
    }
  }

  // Обработчик изменения для DatePicker
  const handleDateChange = (newValue: Dayjs | null) => {
    if (onFieldChange && name) {
      if (newValue && newValue.isValid()) {
        // Сохраняем только дату в формате "YYYY-MM-DD"
        const dateValue = newValue.format('YYYY-MM-DD')
        onFieldChange(name, dateValue)
      } else {
        onFieldChange(name, '')
      }
    }
  }

  // Обработчик изменения для TimePicker
  const handleTimeChange = (newValue: Dayjs | null) => {
    if (onFieldChange && name) {
      if (newValue && newValue.isValid()) {
        // Для времени конвертируем локальное время в UTC и сохраняем в формате "HH:mm:ss+00:00"
        // Используем сегодняшнюю дату как базовую для конвертации в UTC
        const today = dayjs().startOf('day')
        const combined = today
          .hour(newValue.hour())
          .minute(newValue.minute())
          .second(newValue.second())
          .millisecond(newValue.millisecond())
        const utcTime = combined.utc()
        // Форматируем время в формате "HH:mm:ss+00:00"
        const timeValue = utcTime.format('HH:mm:ss') + '+00:00'
        onFieldChange(name, timeValue)
      } else {
        onFieldChange(name, '')
      }
    }
  }

  // Обработчик изменения для DateTimePicker
  const handleDateTimeChange = (newValue: Dayjs | null) => {
    if (onFieldChange && name) {
      if (newValue && newValue.isValid()) {
        // Конвертируем локальное время в UTC и сохраняем в формате ATOM
        // ("YYYY-MM-DDTHH:mm:ss+00:00", без миллисекунд) — storage валидирует
        // datetime-поля строго по date_format:Y-m-d\TH:i:sP
        const utcValue = newValue.utc().format('YYYY-MM-DD[T]HH:mm:ssZ')
        onFieldChange(name, utcValue)
      } else {
        onFieldChange(name, '')
      }
    }
  }

  const applyFiles = (files: File[]) => {
    setFileList(files)
    if (onFieldChange && name) {
      // Если один файл — передаём File, если несколько — массив
      onFieldChange(name, files.length <= 1 ? files[0] || null : files)
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return

    applyFiles(Array.from(files))
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      applyFiles(Array.from(e.dataTransfer.files))
      e.dataTransfer.clearData()
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleRemoveFile = (file: File) => {
    const next = fileList.filter((f) => f !== file)
    applyFiles(next)
  }

  // Формируем inputProps только с включенными настройками
  const inputProps: Record<string, any> = {}
  if (minLengthEnabled && minLength !== undefined) {
    inputProps.minLength = minLength
  }
  if (maxLengthEnabled && maxLength !== undefined) {
    inputProps.maxLength = maxLength
  }

  // Добавляем иконку required справа, если нет label
  const endAdornment =
    !hasLabel && isRequired ? (
      <InputAdornment position="end">
        <ErrorOutlineIcon
          sx={{
            fontSize: '1rem',
            color: 'warning.main',
          }}
        />
      </InputAdornment>
    ) : undefined

  if (type === 'textarea') {
    return (
      <Box data-field-name={name}>
        <TextField
          id={inputId}
          name={name}
          value={value || ''}
          onChange={handleChange}
          onBlur={onBlur}
          placeholder={placeholderEnabled ? placeholder : undefined}
          error={!!error}
          helperText={error}
          multiline
          rows={4}
          fullWidth
          margin="normal"
          inputProps={inputProps}
          InputProps={{
            endAdornment,
          }}
          sx={fieldStyle}
        />
      </Box>
    )
  }

  if (type === 'file') {
    return (
      <Box data-field-name={name}>
        <FormControl fullWidth margin="normal" error={!!error} sx={fieldStyle}>
          {/* Drop zone */}
          <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            sx={{
              border: '1px dashed rgba(0,0,0,0.2)',
              borderRadius: 2,
              padding: 2,
              cursor: 'pointer',
              backgroundColor: 'rgba(0,0,0,0.02)',
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)',
              },
            }}
            onClick={() => {
              const input = document.getElementById(
                inputId
              ) as HTMLInputElement | null
              input?.click()
            }}
          >
            <input
              id={inputId}
              type="file"
              name={name}
              onChange={handleFileChange}
              style={{ display: 'none' }}
              multiple
            />
            <Stack direction="row" spacing={2} alignItems="center">
              <InsertDriveFileIcon color="action" />
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Click to upload or drag and drop
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  You can upload one or multiple files
                </Typography>
              </Box>
              {!hasLabel && isRequired && (
                <ErrorOutlineIcon
                  sx={{
                    fontSize: '1rem',
                    color: 'warning.main',
                    ml: 'auto',
                  }}
                />
              )}
            </Stack>
          </Box>

          {/* Files preview */}
          {fileList.length > 0 && (
            <Stack direction="row" spacing={2} mt={2} flexWrap="wrap">
              {fileList.map((file) => {
                const isImage = file.type.startsWith('image/')
                const objectUrl = URL.createObjectURL(file)

                return (
                  <Card
                    key={file.name + file.lastModified}
                    sx={{ width: 180, position: 'relative' }}
                  >
                    <IconButton
                      size="small"
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        backgroundColor: 'rgba(255,255,255,0.8)',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
                      }}
                      onClick={() => {
                        URL.revokeObjectURL(objectUrl)
                        handleRemoveFile(file)
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                    {isImage ? (
                      <CardMedia
                        component="img"
                        height="120"
                        image={objectUrl}
                        alt={file.name}
                        sx={{ objectFit: 'cover' }}
                      />
                    ) : (
                      <Box
                        sx={{
                          height: 120,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(0,0,0,0.04)',
                        }}
                      >
                        <ImageIcon color="action" fontSize="large" />
                      </Box>
                    )}
                    <CardContent sx={{ py: 1.5, px: 1.5 }}>
                      <Typography
                        variant="body2"
                        noWrap
                        title={file.name}
                        sx={{ maxWidth: '100%' }}
                      >
                        {file.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {(file.size / 1024).toFixed(1)} KB
                      </Typography>
                    </CardContent>
                  </Card>
                )
              })}
            </Stack>
          )}

          {error && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
              {error}
            </Typography>
          )}
        </FormControl>
      </Box>
    )
  }

  // Рендерим DatePicker для типа date
  if (type === 'date') {
    return (
      <Box data-field-name={name}>
        <DatePicker
          label={placeholderEnabled ? placeholder : undefined}
          value={dateValue}
          onChange={handleDateChange}
          slotProps={{
            textField: {
              id: inputId,
              name: name,
              error: !!error,
              helperText: error,
              fullWidth: true,
              margin: 'dense' as const,
              onBlur: onBlur,
              InputProps: {
                endAdornment,
              },
              sx: fieldStyle,
            },
          }}
        />
      </Box>
    )
  }

  // Рендерим TimePicker для типа time
  if (type === 'time') {
    return (
      <Box data-field-name={name}>
        <TimePicker
          label={placeholderEnabled ? placeholder : undefined}
          value={dateValue}
          onChange={handleTimeChange}
          slotProps={{
            textField: {
              id: inputId,
              name: name,
              error: !!error,
              helperText: error,
              fullWidth: true,
              margin: 'dense' as const,
              onBlur: onBlur,
              InputProps: {
                endAdornment,
              },
              sx: fieldStyle,
            },
          }}
        />
      </Box>
    )
  }

  // Рендерим DateTimePicker для типа datetime-local
  if (type === 'datetime-local') {
    return (
      <Box data-field-name={name}>
        <DateTimePicker
          label={placeholderEnabled ? placeholder : undefined}
          value={dateValue}
          onChange={handleDateTimeChange}
          slotProps={{
            textField: {
              id: inputId,
              name: name,
              error: !!error,
              helperText: error,
              fullWidth: true,
              margin: 'dense' as const,
              onBlur: onBlur,
              InputProps: {
                endAdornment,
              },
              sx: fieldStyle,
            },
          }}
        />
      </Box>
    )
  }

  // Для phone field используем специальный компонент
  if (isPhoneField) {
    return (
      <Box data-field-name={name}>
        <PhoneInputWithCountry
          value={value || ''}
          onChange={(newValue: string) => {
            if (onFieldChange) {
              onFieldChange(name, newValue)
            }
          }}
          onBlur={onBlur}
          name={name}
          placeholder={placeholderEnabled ? placeholder : undefined}
          error={!!error}
          helperText={error}
          required={isRequired}
          mask={shouldUseMask ? mask : undefined}
          defaultCountry={defaultCountry}
          fullWidth
          margin="dense"
          sx={fieldStyle}
          onValidationError={handleValidationError}
        />
      </Box>
    )
  }

  return (
    <Box data-field-name={name}>
      <TextField
        id={inputId}
        name={name}
        value={value || ''}
        onChange={handleChange}
        onBlur={onBlur}
        placeholder={placeholderEnabled ? placeholder : undefined}
        error={!!error}
        helperText={error}
        type={type}
        fullWidth
        margin="dense"
        inputProps={inputProps}
        InputProps={{
          endAdornment,
        }}
        sx={fieldStyle}
      />
    </Box>
  )
}
